<template>
    <v-container class="pa-2">
        <v-row dense>
            <v-col cols="12">

                <!-- Header -->
                <v-card class="rounded-xl elevation-2 px-3 py-2 mb-3 sticky-header">
                    <div class="d-flex align-center flex-wrap gap-1">
                        <v-icon color="primary" class="mr-2">mdi-chart-bar</v-icon>
                        <div class="text-subtitle-2 font-weight-bold primary--text flex-grow-1">Statistiche</div>

                        <!-- Toggle scope -->
                        <v-btn-toggle v-if="canToggleScope" v-model="tab" mandatory dense rounded color="primary"
                            @change="onTabChange" class="ml-2">
                            <v-btn :value="0" x-small>
                                <v-icon x-small left>mdi-home-city-outline</v-icon>
                                {{ $store.state.congregazioneCorrente.nome }}
                            </v-btn>
                            <v-btn :value="1" x-small>
                                <v-icon x-small left>mdi-domain</v-icon>
                                Circoscrizione
                            </v-btn>
                            <v-btn v-if="isSuperAdmin" :value="2" x-small>
                                <v-icon x-small left>mdi-earth</v-icon>
                                Globale
                            </v-btn>
                        </v-btn-toggle>

                        <!-- Timestamp cache (tab globale) -->
                        <span v-if="tab === 2 && cacheTs" class="ml-2 text-caption grey--text">
                            Aggiornato alle {{ cacheTs }}
                        </span>
                        <span v-if="tab === 2 && !cacheTs" class="ml-2 text-caption grey--text">
                            <v-progress-circular indeterminate size="12" width="2" color="grey" class="mr-1" />
                            Calcolo in corso...
                        </span>
                        <v-btn v-if="tab === 2 && cacheTs" x-small icon
                            title="Ricalcola ora" class="ml-1" @click="ricalcola">
                            <v-icon x-small>mdi-refresh</v-icon>
                        </v-btn>
                    </div>
                </v-card>

                <!-- KPI row -->
                <v-row dense class="mb-1">
                    <v-col cols="4">
                        <v-card class="rounded-xl elevation-2 pa-3 text-center kpi-card">
                            <div class="text-h5 font-weight-bold primary--text">
                                <span v-if="!loadingKpi">{{ tab === 2 ? kpiGlobal.oratoriTotali : kpi.oratoriTotali }}</span>
                                <v-progress-circular v-else indeterminate size="24" width="2" color="primary" />
                            </div>
                            <div class="text-caption grey--text mt-1">Oratori</div>
                        </v-card>
                    </v-col>
                    <v-col cols="4">
                        <v-card class="rounded-xl elevation-2 pa-3 text-center kpi-card">
                            <div class="text-h5 font-weight-bold success--text">
                                <span v-if="!loadingKpi">{{ tab === 2 ? kpiGlobal.utilizzati : kpi.utilizzati }}</span>
                                <v-progress-circular v-else indeterminate size="24" width="2" color="success" />
                            </div>
                            <div class="text-caption grey--text mt-1">Utilizzati</div>
                        </v-card>
                    </v-col>
                    <v-col cols="4">
                        <v-card class="rounded-xl elevation-2 pa-3 text-center kpi-card">
                            <div class="text-h5 font-weight-bold warning--text">
                                <span v-if="!loadingKpi">{{ tab === 2 ? kpiGlobal.nonUtilizzati : kpi.nonUtilizzati }}</span>
                                <v-progress-circular v-else indeterminate size="24" width="2" color="warning" />
                            </div>
                            <div class="text-caption grey--text mt-1">Non utilizzati</div>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Grafici -->
                <v-row dense>

                    <!-- Connessi ultime 24h — solo globale, full width -->
                    <v-col v-if="tab === 2" cols="12">
                        <v-card class="rounded-xl elevation-2 mb-3">
                            <div class="section-header">
                                <v-icon x-small color="grey darken-1" class="mr-1">mdi-account-clock-outline</v-icon>
                                <span>Distribuzione connessioni ultime 24h</span>
                            </div>
                            <v-card-text class="pt-0">
                                <v-progress-linear v-if="loadingConnessi24h" indeterminate rounded />
                                <GChart v-else-if="graficoConnessi24h.length > 1"
                                    type="LineChart"
                                    :data="graficoConnessi24h"
                                    :options="optConnessi24h"
                                    :settings="{ language: 'it' }" />
                                <div v-else class="text-center grey--text text-caption py-4">Nessun dato</div>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <!-- Ruoli App -->
                    <v-col cols="12" sm="6">
                        <v-card class="rounded-xl elevation-2 mb-3">
                            <div class="section-header">
                                <v-icon x-small color="grey darken-1" class="mr-1">mdi-shield-account-outline</v-icon>
                                <span>Ruoli App</span>
                            </div>
                            <v-card-text class="pt-0">
                                <v-progress-linear v-if="loadingRuoli" indeterminate rounded />
                                <GChart v-else-if="graficoRuoli.length > 1"
                                    type="ColumnChart"
                                    :data="graficoRuoli"
                                    :options="optColumn('#6D4C41')"
                                    :settings="{ language: 'it' }" />
                                <div v-else class="text-center grey--text text-caption py-4">Nessun dato</div>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <!-- Nomine -->
                    <v-col cols="12" sm="6">
                        <v-card class="rounded-xl elevation-2 mb-3">
                            <div class="section-header">
                                <v-icon x-small color="grey darken-1" class="mr-1">mdi-certificate-outline</v-icon>
                                <span>Nomine</span>
                            </div>
                            <v-card-text class="pt-0">
                                <v-progress-linear v-if="loadingNomine" indeterminate rounded />
                                <GChart v-else-if="graficoNomine.length > 1"
                                    type="ColumnChart"
                                    :data="graficoNomine"
                                    :options="optColumn('orange')"
                                    :settings="{ language: 'it' }" />
                                <div v-else class="text-center grey--text text-caption py-4">Nessun dato</div>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <!-- Ultimi connessi -->
                    <v-col cols="12" sm="6">
                        <v-card class="rounded-xl elevation-2 mb-3">
                            <div class="section-header d-flex align-center">
                                <v-icon x-small color="grey darken-1" class="mr-1">mdi-login-variant</v-icon>
                                <span class="flex-grow-1">Ultimi connessi</span>
                                <v-btn-toggle v-model="web" mandatory dense rounded color="primary"
                                    @change="loadOratori" class="mr-2" style="height:22px">
                                    <v-btn :value="false" x-small style="height:22px;min-width:38px">App</v-btn>
                                    <v-btn :value="true"  x-small style="height:22px;min-width:38px">Web</v-btn>
                                </v-btn-toggle>
                            </div>
                            <v-card-text class="pt-0">
                                <v-progress-linear v-if="loadingOratori" indeterminate rounded />
                                <v-data-table v-else
                                    :headers="tab === 2 ? headersOratoriGlobali : headersOratori"
                                    :items="listaOratori"
                                    :items-per-page="10"
                                    dense
                                    hide-default-header
                                    :mobile-breakpoint="0"
                                    no-data-text="Nessun dato"
                                    style="background: transparent"
                                    :footer-props="{ 'items-per-page-options': [10, 20] }">
                                    <template v-slot:item.nominativo="{ item }">
                                        <div class="text-caption font-weight-medium">{{ item.nominativo }}</div>
                                        <div class="text-caption grey--text">
                                            {{ item.congregazione }}
                                            <span v-if="tab === 2 && item.circoscrizione"> · {{ item.circoscrizione }}</span>
                                        </div>
                                        <div class="text-caption" v-html="item.data ? localDataTime(item.data) : '—'" />
                                    </template>
                                </v-data-table>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <!-- Appuntamenti per Oratore / Circoscrizione -->
                    <v-col cols="12" sm="6">
                        <v-card class="rounded-xl elevation-2 mb-3">
                            <div class="section-header d-flex align-center">
                                <v-icon x-small color="grey darken-1" class="mr-1">mdi-account-group-outline</v-icon>
                                <span class="flex-grow-1">
                                    Appuntamenti per {{ tab === 2 ? 'Circoscrizione' : 'Oratore' }}
                                </span>
                                <v-btn-toggle v-if="tab !== 2" v-model="futuri" mandatory dense rounded color="primary"
                                    @change="loadProgrammi" class="mr-2" style="height:22px">
                                    <v-btn :value="false" x-small style="height:22px;min-width:60px">Ultimo anno</v-btn>
                                    <v-btn :value="true"  x-small style="height:22px;min-width:48px">Futuri</v-btn>
                                </v-btn-toggle>
                            </div>
                            <v-card-text class="pt-0">
                                <v-progress-linear v-if="loadingProgrammi" indeterminate rounded />
                                <GChart v-else-if="graficoProgrammi.length > 1"
                                    type="BarChart"
                                    :data="graficoProgrammi"
                                    :options="optBar"
                                    :settings="{ language: 'it' }" />
                                <div v-else class="text-center grey--text text-caption py-4">Nessun dato</div>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <!-- Appuntamenti per Congregazione (solo tab 0 e 1) -->
                    <v-col v-if="tab !== 2" cols="12" sm="6">
                        <v-card class="rounded-xl elevation-2 mb-3">
                            <div class="section-header d-flex align-center">
                                <v-icon x-small color="grey darken-1" class="mr-1">mdi-home-city-outline</v-icon>
                                <span class="flex-grow-1">Appuntamenti per Congregazione</span>
                                <v-btn-toggle v-model="futuriCong" mandatory dense rounded color="primary"
                                    @change="loadProgrammiCong" class="mr-2" style="height:22px">
                                    <v-btn :value="false" x-small style="height:22px;min-width:60px">Ultimo anno</v-btn>
                                    <v-btn :value="true"  x-small style="height:22px;min-width:48px">Futuri</v-btn>
                                </v-btn-toggle>
                            </div>
                            <v-card-text class="pt-0">
                                <v-progress-linear v-if="loadingProgrammiCong" indeterminate rounded />
                                <GChart v-else-if="graficoProgrammiCong.length > 1"
                                    type="BarChart"
                                    :data="graficoProgrammiCong"
                                    :options="optBarCong"
                                    :settings="{ language: 'it' }" />
                                <div v-else class="text-center grey--text text-caption py-4">Nessun dato</div>
                            </v-card-text>
                        </v-card>
                    </v-col>

                </v-row>

            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { callService, gLocalDataTime, SUPERADMIN_CODE } from '@/modules/utilities.js'
import { GChart } from 'vue-google-charts/legacy'

export default {
    name: 'Statistiche',
    components: { GChart },

    data: () => ({
        tab: 1,         // 0=congregazione, 1=circoscrizione, 2=globale
        futuri: false,
        web: false,
        cacheTs: null,
        _cachePollTimer: null,

        loadingKpi: false,
        kpi:       { oratoriTotali: 0, utilizzati: 0, nonUtilizzati: 0 },
        kpiGlobal: { oratoriTotali: 0, utilizzati: 0, nonUtilizzati: 0 },

        loadingRuoli: false,
        graficoRuoli: [],

        loadingNomine: false,
        graficoNomine: [],

        loadingProgrammi: false,
        graficoProgrammi: [],

        loadingProgrammiCong: false,
        graficoProgrammiCong: [],
        futuriCong: false,

        loadingOratori: false,
        listaOratori: [],

        loadingConnessi24h: false,
        connessi24h: { app: [], web: [] },

        headersOratori:       [{ text: 'Nominativo', value: 'nominativo' }],
        headersOratoriGlobali:[{ text: 'Nominativo', value: 'nominativo' }],
    }),

    computed: {
        isSuperAdmin() {
            return this.$store.state.oratore?.codice == SUPERADMIN_CODE ||
                   this.$store.state.loginCodice == SUPERADMIN_CODE ||
                   this.$store.state.loginCodice == 250613;
        },
        canToggleScope() {
            const o = this.$store.state.oratore;
            return o?.codice == SUPERADMIN_CODE || o?.isAdmin;
        },
        allCong() {
            return this.tab === 1; // tab 1 = tutta la circoscrizione
        },
        chartTextColor() {
            return this.$vuetify.theme.dark ? 'white' : 'black';
        },
        graficoConnessi24h() {
            const app = this.connessi24h.app;
            const web = this.connessi24h.web;
            if (!app.length || !web.length) return [];
            const nowHour = new Date().getHours(); // ora locale del client
            const rows = [['Ora', 'App', 'Web']];
            for (let i = 23; i >= 0; i--) { // da 24h fa → ora
                const h = (nowHour - i + 24) % 24;
                rows.push([String(h).padStart(2, '0') + ':00', app[i], web[i]]);
            }
            return rows;
        },
        optConnessi24h() {
            return {
                legend: { position: 'right' },
                backgroundColor: { fill: 'transparent' },
                colors: ['#1565C0', '#00897B'],
                vAxis: { textStyle: { color: this.chartTextColor }, format: '0', minValue: 0 },
                hAxis: { textStyle: { color: this.chartTextColor, fontSize: 10 }, slantedText: true, slantedTextAngle: 45 },
                chartArea: { width: '80%', height: '65%' },
                height: 260,
                curveType: 'function',
                pointSize: 4,
            };
        },
        optBar() {
            const n = this.graficoProgrammi.length - 1;
            const h = n > 15 ? 600 : n > 8 ? 400 : 280;
            return {
                height: h,
                legend: { position: 'none' },
                backgroundColor: { fill: 'transparent' },
                colors: ['#1565C0'],
                vAxis: { textStyle: { fontSize: 9, color: this.chartTextColor } },
                hAxis: { textStyle: { color: this.chartTextColor }, format: '0' },
                chartArea: { width: '60%', height: '90%' }
            };
        },
        optBarCong() {
            const n = this.graficoProgrammiCong.length - 1;
            const h = n > 15 ? 600 : n > 8 ? 400 : 280;
            return {
                height: h,
                legend: { position: 'none' },
                backgroundColor: { fill: 'transparent' },
                colors: ['#00897B'],
                vAxis: { textStyle: { fontSize: 9, color: this.chartTextColor } },
                hAxis: { textStyle: { color: this.chartTextColor }, format: '0' },
                chartArea: { width: '60%', height: '90%' }
            };
        },
    },

    methods: {
        localDataTime(v) { return gLocalDataTime(v); },

        optColumn(color) {
            return {
                legend: { position: 'none' },
                backgroundColor: { fill: 'transparent' },
                colors: [color],
                vAxis: { textStyle: { color: this.chartTextColor } },
                hAxis: { textStyle: { color: this.chartTextColor } },
            };
        },

        // all=true solo per tab 2, allCong rispecchia il tab
        baseParam(extra = []) {
            const all = this.tab === 2;
            return [
                this.$store.state.idCircoscrizione,
                this.$store.state.idCongregazione,
                all,
                this.allCong,
                ...extra,
            ];
        },

        async loadCacheTs() {
            if (this.tab !== 2) { this.cacheTs = null; return; }
            const res = await callService('/api/dashboard/cacheinfo', []);
            if (res && res.ultimoAggiornamento) {
                const d = new Date(res.ultimoAggiornamento);
                this.cacheTs = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
                this.kpiGlobal = { oratoriTotali: res.oratoriTotali, utilizzati: res.utilizzati, nonUtilizzati: res.nonUtilizzati };
                clearInterval(this._cachePollTimer);
                this._cachePollTimer = null;
            } else {
                this.cacheTs = null;
                if (!this._cachePollTimer) {
                    this._cachePollTimer = setInterval(async () => {
                        if (this.tab !== 2) { clearInterval(this._cachePollTimer); this._cachePollTimer = null; return; }
                        await this.loadCacheTs();
                        if (this.cacheTs) this.loadAll();
                    }, 10000);
                }
            }
        },

        async loadKpi() {
            if (this.tab === 2) return; // nessun kpi globale
            this.loadingKpi = true;
            const res = await callService('/api/dashboard/kpi', this.baseParam());
            if (res) this.kpi = res;
            this.loadingKpi = false;
        },

        async loadRuoli() {
            this.loadingRuoli = true;
            const res = await callService('/api/dashboard/ruoli', this.baseParam());
            this.graficoRuoli = res || [];
            this.loadingRuoli = false;
        },

        async loadNomine() {
            this.loadingNomine = true;
            const res = await callService('/api/dashboard/nomine', this.baseParam());
            this.graficoNomine = res || [];
            this.loadingNomine = false;
        },

        async loadProgrammi() {
            this.loadingProgrammi = true;
            const res = await callService('/api/dashboard/programmi', this.baseParam([false, this.futuri]));
            this.graficoProgrammi = res || [];
            this.loadingProgrammi = false;
        },

        async loadProgrammiCong() {
            if (this.tab === 2) return;
            this.loadingProgrammiCong = true;
            const res = await callService('/api/dashboard/programmiCong', this.baseParam([this.futuriCong]));
            this.graficoProgrammiCong = res || [];
            this.loadingProgrammiCong = false;
        },

        async loadOratori() {
            this.loadingOratori = true;
            const res = await callService('/api/dashboard/oratori', this.baseParam([this.web]));
            this.listaOratori = res || [];
            this.loadingOratori = false;
        },

        async loadConnessi24h() {
            if (this.tab !== 2) return;
            this.loadingConnessi24h = true;
            const res = await callService('/api/dashboard/connessi24h', []);
            if (res && Array.isArray(res.app)) this.connessi24h = res;
            this.loadingConnessi24h = false;
        },

        loadAll() {
            this.loadKpi();
            this.loadRuoli();
            this.loadNomine();
            this.loadProgrammi();
            this.loadProgrammiCong();
            this.loadOratori();
            this.loadConnessi24h();
        },

        async ricalcola() {
            this.cacheTs = null;
            await callService('/api/dashboard/ricalcola', []);
            this.loadCacheTs();
        },

        onTabChange() {
            if (this.tab === 2) {
                this.loadCacheTs();
            } else {
                this.cacheTs = null;
                clearInterval(this._cachePollTimer);
                this._cachePollTimer = null;
            }
            this.loadAll();
        },
    },

    created() {
        const o = this.$store.state.oratore;
        const superAdmin = o?.codice == SUPERADMIN_CODE;
        const isAdmin = o?.isAdmin;
        // default: circoscrizione per admin, congregazione per gli altri
        this.tab = (superAdmin || isAdmin) ? 1 : 0;
        this.loadAll();
    },

    beforeDestroy() {
        clearInterval(this._cachePollTimer);
    },
}
</script>

<style scoped>
.sticky-header {
    position: sticky;
    top: 0;
    z-index: 4;
}
.section-header {
    display: flex;
    align-items: center;
    padding: 10px 14px 6px;
    font-size: 0.72rem;
    font-weight: 700;
    color: rgba(0,0,0,0.5);
    letter-spacing: 0.03em;
    text-transform: uppercase;
}
.theme--dark .section-header {
    color: rgba(255,255,255,0.45);
}
.kpi-card {
    min-height: 70px;
}
</style>
